import { effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { Filter, PreviewCustomRule } from '../models/filter-models';
import { ToggleStateService } from './toggle-state.service';
import { ItemLibraryService } from './item-library.service';
import { RandomLootService } from './random-loot.service';
import { SimulatorService } from './simulator.service';
import { NavigationService } from './navigation.service';
import { FilterService } from './filter.service';
import { ItemDataService } from './item-data.service';
import { CurrencyTierService } from './tiering/currency-tier.service';
import { UniquesTierService } from './tiering/uniques-tier.service';
import { EndgameContentPresetService } from './endgame-content-preset.service';
import { LabelLayoutStyle } from '../common/rule-preview/label-layout.helper';
import { poe1 } from '../../game';
import { GameItem } from '../models/game-item.models';
import { Tier, UniqueTierType } from '../models/item-data-models';
import { SettingsService } from './settings.service';
import {
  buildDropPreviewRuleGroupsAsync,
  DROP_PREVIEW_SECTION_ORDER,
  emptyDropPreviewRuleGroups,
} from './drop-preview-generation.util';
import {
  DropPreviewRuleGroups,
  DropPreviewSection,
  DropPreviewSectionVisibility,
} from './drop-preview.models';
import { LabelPreviewBackgroundId } from '../common/label-preview-backgrounds';
import { Presets } from '../models/filter-models';
import { areaLevelForProgressPreset } from './presets.service';

const prefix = poe1 ? 'poe1' : 'poe';
export const DROP_PREVIEW_AREA_LEVEL_MAX = poe1 ? 90 : 85;
const KEY_LAYOUT = `${prefix}-rule-preview-layout`;
const KEY_BEAMS = `${prefix}-drop-preview-beams`;
const KEY_FORCE_GLOW = `${prefix}-drop-preview-force-glow`;
const KEY_AREA_LEVEL = `${prefix}-drop-preview-area-level`;
const TOGGLE_KEY = 'poe-preview-tab';

const DEFAULT_SECTION_VISIBILITY: DropPreviewSectionVisibility = {
  itemLibrary: true,
  randomLoot: true,
  currencyDropTiers: true,
  ...(poe1 ? { divinationCardDropTiers: true } : {}),
  uniqueDropTiers: true,
  gold: true,
  customCosmeticRules: true,
  customRules: true,
};

@Injectable({
  providedIn: 'root',
})
export class DropPreviewService {
  private readonly filterService = inject(FilterService);
  private readonly currencyTierService = inject(CurrencyTierService);
  private readonly uniquesTierService = inject(UniquesTierService);
  private readonly endgameContentPresetService = inject(EndgameContentPresetService);
  private readonly itemDataService = inject(ItemDataService);
  private readonly toggleStateService = inject(ToggleStateService);
  private readonly itemLibraryService = inject(ItemLibraryService);
  private readonly randomLootService = inject(RandomLootService);
  private readonly simulatorService = inject(SimulatorService);
  private readonly navigationService = inject(NavigationService);
  private readonly settingsService = inject(SettingsService);
  private readonly ngZone = inject(NgZone);

  readonly ruleGroups = signal<DropPreviewRuleGroups>(emptyDropPreviewRuleGroups());
  readonly generating = signal(false);
  readonly globalSectionVisibility = signal<DropPreviewSectionVisibility>(
    this.toggleStateService.loadStates(TOGGLE_KEY, { ...DEFAULT_SECTION_VISIBILITY }),
  );
  readonly beams = signal(this.loadBeams());
  readonly forceGlow = signal<boolean | null>(this.loadForceGlow());
  readonly labelLayoutStyle = signal<LabelLayoutStyle>(this.loadLayoutStyle());
  readonly labelPreviewBg = this.settingsService.labelPreviewBg;
  readonly itemDesignerRequest = signal<{ item?: GameItem } | null>(null);
  readonly areaLevel = signal<number | null>(this.loadAreaLevel());

  private generationToken = 0;

  constructor() {
    effect(() => {
      this.filterService.filterResultData();
      this.filterService.activeFilter();
      this.itemLibraryService.items();
      this.randomLootService.items();
      this.areaLevel();
      void this.regenerate();
    });
  }

  setAreaLevel(level: number | null): void {
    if (level === null) {
      this.areaLevel.set(null);
      localStorage.removeItem(KEY_AREA_LEVEL);
      return;
    }
    const clamped = Math.min(DROP_PREVIEW_AREA_LEVEL_MAX, Math.max(1, Math.round(level)));
    this.areaLevel.set(clamped);
    localStorage.setItem(KEY_AREA_LEVEL, String(clamped));
  }

  setAreaLevelFromPreset(preset: Presets): void {
    this.setAreaLevel(areaLevelForProgressPreset(preset, !poe1));
  }

  resolveVisibleGroups(override?: DropPreviewSectionVisibility | null): {
    rules: PreviewCustomRule[];
    ruleGroups: PreviewCustomRule[][];
  } {
    const visibility = { ...this.globalSectionVisibility(), ...override };
    const groups = this.ruleGroups();
    const visibleGroups: PreviewCustomRule[][] = [];
    let order = 0;

    for (const section of DROP_PREVIEW_SECTION_ORDER) {
      if (section === 'divinationCardDropTiers' && !poe1) continue;
      if (!visibility[section]) continue;

      const sectionRules = groups[section];
      if (sectionRules.length === 0) continue;

      const orderedGroup: PreviewCustomRule[] = [];
      for (const rule of sectionRules) {
        const clone = new PreviewCustomRule(rule);
        clone.order = order++;
        orderedGroup.push(clone);
      }
      visibleGroups.push(orderedGroup);
    }

    return {
      rules: visibleGroups.flat(),
      ruleGroups: visibleGroups,
    };
  }

  async regenerate(): Promise<void> {
    const token = ++this.generationToken;
    this.generating.set(true);

    const filter = this.filterService.activeFilter().filter;
    const context = {
      filter,
      libraryItems: this.itemLibraryService.items(),
      lootItems: this.randomLootService.items(),
      poe1,
      lookupItemClass: (baseType: string) => this.lookupItemClass(baseType),
      getFirstCurrencyForDropTier: (tier: Tier) => this.currencyTierService.getFirstCurrencyForDropTier(tier),
      getFirstDivinationCardForDivinationCardTier: (tier: Tier) =>
        this.currencyTierService.getFirstCurrencyForDropTier(tier, true),
      getFirstUniqueForUniqueTier: (tier: UniqueTierType) => this.getFirstUniqueForUniqueTier(tier),
      getFirstFavouriteRestrictedBossUniqueBaseType: (excludeBaseTypes: ReadonlySet<string>) =>
        this.getFirstFavouriteRestrictedBossUniqueBaseType(filter, excludeBaseTypes),
      getFirstFavouriteLowValueBossUniqueBaseType: (excludeBaseTypes: ReadonlySet<string>) =>
        this.getFirstFavouriteLowValueBossUniqueBaseType(filter, excludeBaseTypes),
      getFirstOtherLowValueBossUniqueBaseType: (excludeBaseTypes: ReadonlySet<string>) =>
        this.getFirstOtherLowValueBossUniqueBaseType(filter, excludeBaseTypes),
      simulate: (item: GameItem, cacheKey?: string) =>
        this.simulatorService.simulate(item, cacheKey, this.areaLevel()),
    };

    const isStale = () => token !== this.generationToken;

    const result = await this.ngZone.runOutsideAngular(() =>
      buildDropPreviewRuleGroupsAsync(context, isStale),
    );

    if (isStale() || result === null) {
      if (token === this.generationToken) {
        this.ngZone.run(() => this.generating.set(false));
      }
      return;
    }

    this.ngZone.run(() => {
      if (token !== this.generationToken) return;
      this.ruleGroups.set(result);
      this.generating.set(false);
    });
  }

  toggleSection(section: DropPreviewSection): void {
    const visibility = { ...this.globalSectionVisibility() };
    this.toggleStateService.toggle(TOGGLE_KEY, visibility, section);
    this.globalSectionVisibility.set(visibility);
  }

  ensureSectionEnabled(section: DropPreviewSection): void {
    if (this.globalSectionVisibility()[section]) return;

    const visibility = { ...this.globalSectionVisibility(), [section]: true };
    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  toggleRandomLoot(): void {
    const wasOff = !this.globalSectionVisibility()['randomLoot'];
    const visibility = { ...this.globalSectionVisibility() };
    this.toggleStateService.toggle(TOGGLE_KEY, visibility, 'randomLoot');
    this.globalSectionVisibility.set(visibility);
    if (wasOff && this.randomLootService.items().length === 0) {
      void this.randomLootService.generateLootPile();
    }
  }

  readonly dropTierSectionToggleKeys = (
    [
      'currencyDropTiers',
      ...(poe1 ? ['divinationCardDropTiers' as const] : []),
      'uniqueDropTiers',
      'gold',
    ] as const
  );

  readonly customRuleSectionToggleKeys = (
    [
      'customCosmeticRules',
      'customRules',
    ] as const
  );

  readonly fixedSectionToggleKeys = (
    [
      ...this.dropTierSectionToggleKeys,
      ...this.customRuleSectionToggleKeys,
    ] as const
  );

  allDropTierTogglesOn(): boolean {
    return this.dropTierSectionToggleKeys.every((key) => this.globalSectionVisibility()[key]);
  }

  toggleAllDropTierSections(): void {
    this.toggleSectionGroup(this.dropTierSectionToggleKeys);
  }

  allCustomRuleTogglesOn(): boolean {
    return this.customRuleSectionToggleKeys.every((key) => this.globalSectionVisibility()[key]);
  }

  toggleAllCustomRuleSections(): void {
    this.toggleSectionGroup(this.customRuleSectionToggleKeys);
  }

  initializeSections(sections: DropPreviewSectionVisibility): void {
    const visibility = { ...this.globalSectionVisibility(), ...sections };
    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  anyDropTiersVisible(): boolean {
    return this.dropTierSectionToggleKeys.some(key => this.globalSectionVisibility()[key]);
  }

  showOnlyItemLibrary(): void {
    const visibility = { ...this.globalSectionVisibility() };
    visibility.itemLibrary = true;
    visibility.randomLoot = false;
    for (const key of this.fixedSectionToggleKeys) {
      visibility[key] = false;
    }
    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  showOnlyRandomLoot(): void {
    const visibility = { ...this.globalSectionVisibility() };
    visibility.itemLibrary = false;
    visibility.randomLoot = true;
    for (const key of this.fixedSectionToggleKeys) {
      visibility[key] = false;
    }
    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  showOnlyDropTiers(): void {
    const visibility = { ...this.globalSectionVisibility() };
    visibility.itemLibrary = false;
    visibility.randomLoot = false;
    for (const key of this.dropTierSectionToggleKeys) {
      visibility[key] = true;
    }
    for (const key of this.customRuleSectionToggleKeys) {
      visibility[key] = false;
    }
    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  private toggleSectionGroup(keys: readonly DropPreviewSection[]): void {
    const newState = !keys.every((key) => this.globalSectionVisibility()[key]);
    const visibility = { ...this.globalSectionVisibility() };

    for (const key of keys) {
      visibility[key] = newState;
    }

    this.globalSectionVisibility.set(visibility);
    this.toggleStateService.saveStates(TOGGLE_KEY, visibility);
  }

  toggleBeams(): void {
    this.beams.update(v => {
      const next = !v;
      localStorage.setItem(KEY_BEAMS, String(next));
      return next;
    });
  }

  toggleForceGlow(): void {
    const current = this.forceGlow();
    const on = current === true || (current === null && this.settingsService.labelGlow() === 'always');
    const next = on ? false : true;
    this.forceGlow.set(next);
    localStorage.setItem(KEY_FORCE_GLOW, String(next));
  }

  toggleLabelLayout(): void {
    this.labelLayoutStyle.update(style => {
      const next: LabelLayoutStyle = style === 'stacked' ? 'scattered' : 'stacked';
      localStorage.setItem(KEY_LAYOUT, next);
      return next;
    });
  }

  setLabelPreviewBg(value: LabelPreviewBackgroundId): void {
    this.settingsService.setLabelPreviewBg(value);
  }

  onItemLibraryClick(event: MouseEvent, rule: PreviewCustomRule): void {
    if (event.ctrlKey || event.metaKey) {
      const completeId = rule.librarySimulatorResult?.rule?.completeId;
      if (completeId) {
        event.preventDefault();
        this.navigationService.navigateToFilterTitle(
          completeId,
          rule.librarySimulatorResult?.item.baseType,
        );
      }
      return;
    }
    if (rule.isSimulatedTierItem) return;
    if (event.altKey) {
      if (!rule.librarySimulatorResult) return;
      event.preventDefault();
      this.itemDesignerRequest.set({ item: new GameItem(rule.librarySimulatorResult.item) });
      return;
    }
    if (rule.isRandomLootItem && rule.librarySimulatorResult) {
      event.preventDefault();
      const item = rule.librarySimulatorResult.item;
      const itemText = rule.itemLibraryText ?? JSON.stringify(item);
      void this.itemLibraryService.add(item, itemText);
      return;
    }

    if (!rule.isItemLibraryItem || !rule.itemLibraryHash) return;
    event.preventDefault();
    this.itemLibraryService.removeByHash(rule.itemLibraryHash);
  }

  openItemDesigner(): void {
    this.ensureSectionEnabled('itemLibrary');
    this.itemDesignerRequest.set({});
  }

  clearItemDesignerRequest(): void {
    this.itemDesignerRequest.set(null);
  }

  private getFirstUniqueForUniqueTier(tier: UniqueTierType): string {
    const tierEntries = this.uniquesTierService.getTieredUniques()[tier];
    for (const entry of tierEntries) {
      if (this.lookupItemClass(entry.baseType)) return entry.baseType;
    }
    return tierEntries[0]?.baseType ?? 'Unique';
  }

  private getFirstFavouriteRestrictedBossUniqueBaseType(
    filter: Filter,
    excludeBaseTypes: ReadonlySet<string>,
  ): string | undefined {
    if (filter.favouriteEndgameContent.includes('All') || !filter.showFavouriteRestrictedBossUniques) {
      return undefined;
    }

    const restrictedActive = filter.uniqueTierCosmetics[3].displayOptions.active && !filter.uniqueTierCosmetics[3].displayOptions.hidden;
    if (restrictedActive) return undefined;

    const baseTypes = this.endgameContentPresetService.getFavouriteRestrictedBossBaseTypes(
      filter,
      this.uniquesTierService.getTieredUniques()['Restricted'],
    );
    for (const baseType of baseTypes) {
      if (excludeBaseTypes.has(baseType)) continue;
      if (this.lookupItemClass(baseType)) return baseType;
    }
    return undefined;
  }

  private getFirstFavouriteLowValueBossUniqueBaseType(
    filter: Filter,
    excludeBaseTypes: ReadonlySet<string>,
  ): string | undefined {
    if (filter.favouriteEndgameContent.includes('All') || !filter.showFavouriteLowValueBossUniques || !filter.hideAllRemainingUniques) {
      return undefined;
    }

    const otherActive = filter.uniqueTierCosmetics[4].displayOptions.active && !filter.uniqueTierCosmetics[4].displayOptions.hidden;
    if (otherActive) return undefined;

    const baseTypes = this.endgameContentPresetService.getFavouriteLowValueBossBaseTypes(
      filter,
      this.uniquesTierService.getTieredUniques()['Other'],
    );
    for (const baseType of baseTypes) {
      if (excludeBaseTypes.has(baseType)) continue;
      if (this.lookupItemClass(baseType)) return baseType;
    }
    return undefined;
  }

  private getFirstOtherLowValueBossUniqueBaseType(
    filter: Filter,
    excludeBaseTypes: ReadonlySet<string>,
  ): string | undefined {
    if (!filter.showOtherLowValueBossUniques || !filter.hideAllRemainingUniques) {
      return undefined;
    }

    const otherActive = filter.uniqueTierCosmetics[4].displayOptions.active && !filter.uniqueTierCosmetics[4].displayOptions.hidden;
    if (otherActive) return undefined;

    const baseTypes = this.endgameContentPresetService.getOtherLowValueBossBaseTypes(
      filter,
      this.uniquesTierService.getTieredUniques()['Other'],
    );
    for (const baseType of baseTypes) {
      if (excludeBaseTypes.has(baseType)) continue;
      if (this.lookupItemClass(baseType)) return baseType;
    }
    return undefined;
  }

  private lookupItemClass(baseType: string): string | undefined {
    const fromFlat = this.itemDataService.getFlatItemDataByBaseTypeName(baseType)?.itemClass;
    if (fromFlat) return fromFlat;

    const unique = this.itemDataService.getUniquesList().find(u => u.baseType === baseType);
    if (!unique) return undefined;

    return this.inferItemClassFromUniqueCategory(unique.category, baseType);
  }

  private inferItemClassFromUniqueCategory(category: string, baseType: string): string | undefined {
    switch (category) {
      case 'Jewels':
        return 'Jewels';
      case 'Maps':
        return poe1 ? 'Maps' : 'Tablets';
      case 'Other':
        return 'Relics';
      case 'Jewellery':
        if (baseType === 'Ring') return 'Rings';
        if (baseType.includes('Amulet')) return 'Amulets';
        if (baseType.includes('Belt')) return 'Belts';
        return 'Rings';
      default:
        return undefined;
    }
  }

  private loadLayoutStyle(): LabelLayoutStyle {
    const raw = localStorage.getItem(KEY_LAYOUT);
    return raw === 'stacked' || raw === 'scattered' ? raw : 'stacked';
  }

  private loadBeams(): boolean {
    const raw = localStorage.getItem(KEY_BEAMS);
    return raw === null ? true : raw === 'true';
  }

  private loadForceGlow(): boolean | null {
    const raw = localStorage.getItem(KEY_FORCE_GLOW);
    if (raw === null) return null;
    return raw === 'true';
  }

  private loadAreaLevel(): number | null {
    const raw = localStorage.getItem(KEY_AREA_LEVEL);
    if (raw === null || raw === '') return null;

    const parsed = Math.round(Number(raw));
    if (!Number.isFinite(parsed)) return null;

    return Math.min(DROP_PREVIEW_AREA_LEVEL_MAX, Math.max(1, parsed));
  }
}
